import React, { useEffect, useState } from 'react';
import { Grid, Box, Typography, Button, Badge, Link } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import { HistorySectionProps, ExtendedDataProvider, HistorySaleState } from '../utils/SingleEventAnalysisTab.types';
import MDialog from '../../../../../components/controls/MDialog';
import EventHistoryTable from '../EventHistoryTable';
import PrimarySaleChart from '../../PrimarySaleChart';
import GetHistorySale from '../GetHistorySale';

/**
 * HistorySection Component
 *
 * Smart component that manages and displays historical analysis data for events.
 * Independently fetches history sale data via useEffect when event is selected.
 *
 * Features:
 * - Independent data fetching on event change
 * - Historical event data display via EventHistoryTable
 * - Primary sales chart visualization
 * - History sale modal with detailed transaction data
 * - Badge counter showing number of history records
 *
 * @param props - Component props containing selected event
 * @returns Historical data analysis section
 */
const HistorySection: React.FC<HistorySectionProps> = ({ selectedEvent }) => {
    const dataProvider = useDataProvider() as unknown as ExtendedDataProvider;
    const notify = useNotify();

    // History Sale Modal State
    const [historySaleState, setHistorySaleState] = useState<HistorySaleState>({
        open: false,
        loading: false,
        error: null,
        data: []
    });

    /**
     * Fetches historical sales data from the backend API
     */
    const getDataHistorySale = async () => {
        if (!selectedEvent?.siteId) return;
        try {
            const response = await dataProvider.postData('RTP_Event', 'GetHistorySale', {
                siteId: selectedEvent.siteId,
                dateUtc: selectedEvent.dateUtc,
                name: selectedEvent.name,
                venue: selectedEvent.venue
            });
            setHistorySaleState((prev) => ({
                ...(prev as HistorySaleState),
                error: null,
                data: response.data as any[] || []
            }));
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            notify(`Error fetching history sale data: ${errorMessage}`, { type: 'error' });
            setHistorySaleState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage || 'Failed to load historical sales data',
                data: []
            }));
        }
    };

    useEffect(() => {
        // Load history sale data when event changes
        if (selectedEvent) getDataHistorySale();
    }, [selectedEvent]);

    /**
     * Handles the "Show History Sale" button click
     * Opens the history sale modal
     */
    const handleShowHistorySale = async () => {
        setHistorySaleState((prev) => ({
            ...prev,
            open: true,
            error: null
        }));
    };

    /**
     * Closes the history sale modal
     */
    const closeHistorySaleModal = () => {
        setHistorySaleState((prev) => ({
            ...prev,
            open: false,
            error: null,
        }));
    };

    return (
        <>
            <Grid size={12} paddingTop={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 0 }}>
                        📊 Historical Data Overview
                    </Typography>
                    {selectedEvent?.siteId && (
                        <Badge badgeContent={historySaleState.data.length} color='warning' showZero>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<HistoryIcon />}
                                onClick={handleShowHistorySale}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 2,
                                    '&:hover': {
                                        boxShadow: 4
                                    }
                                }}
                            >
                                Show History Sale
                            </Button>
                        </Badge>
                    )}
                </Box>
                <EventHistoryTable selectedEvent={selectedEvent} />
                {selectedEvent?.siteId && (
                    <PrimarySaleChart siteId={String(selectedEvent.siteId)} marketType={selectedEvent.type || ''} />
                )}
            </Grid>

            {/* History Sale Dialog */}
            <MDialog
                name={'HistorySaleDialog'}
                height={'auto'}
                width={'lg'}
                action={null}
                disabled={null}
                msgLoading={null}
                customHeader={null}
                open={historySaleState.open}
                title={<Link underline="hover" href={selectedEvent?.primary?.url} target="_blank" >#{selectedEvent?.siteId}</Link>}
                handleClose={closeHistorySaleModal}
                maxWidth={'xl'}>
                <GetHistorySale
                    data={historySaleState.data}
                    loading={historySaleState.loading}
                    error={historySaleState.error}
                    querySiteId={selectedEvent?.siteId}
                />
            </MDialog>
        </>
    );
};

export default HistorySection;
